import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount } from 'wagmi';
import { Link } from 'react-router-dom';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'az', name: 'Azərbaycan', flag: '🇦🇿' },
  { code: 'kk', name: 'Қазақша', flag: '🇰🇿' },
  { code: 'uz', name: "O'zbek", flag: '🇺🇿' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
];

const NAV_ITEMS = [
  { id: 'strategies', key: 'nav.strategies' },
  { id: 'calculator', key: 'nav.calculator' },
  { id: 'partners', key: 'nav.partners' },
  { id: 'security', key: 'nav.security' },
  { id: 'faq', key: 'nav.faq' },
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);
  const { isConnected } = useAccount();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLangDropdownOpen(false);
  };

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`sf-navbar ${isScrolled ? 'sf-navbar--scrolled' : ''}`}>
      <div className="container">
        <div className="sf-navbar__inner">
          {/* Logo */}
          <a
            className="sf-navbar__logo"
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('hero');
            }}
            aria-label="Scrooge Finance home"
          >
            <span className="sf-navbar__logo-mark" aria-hidden="true">
              <svg viewBox="0 0 32 32" width="28" height="28">
                <defs>
                  <linearGradient id="navLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FFD56B" />
                    <stop offset="100%" stopColor="#E0A820" />
                  </linearGradient>
                </defs>
                <polygon
                  points="16,2 28,9 28,23 16,30 4,23 4,9"
                  fill="none"
                  stroke="url(#navLogoGrad)"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <text
                  x="16"
                  y="22"
                  textAnchor="middle"
                  fontFamily="Inter, sans-serif"
                  fontSize="16"
                  fontWeight="900"
                  fill="url(#navLogoGrad)"
                >
                  S
                </text>
              </svg>
            </span>
            <span className="sf-navbar__logo-text">SCROOGE</span>
            <span className="sf-navbar__logo-sub">FINANCE</span>
          </a>

          {/* Desktop nav */}
          <ul className="sf-navbar__nav">
            {NAV_ITEMS.map((item) => (
              <li key={item.id}>
                <a
                  className="sf-navbar__link"
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.id);
                  }}
                >
                  {t(item.key)}
                </a>
              </li>
            ))}
          </ul>

          {/* Right side */}
          <div className="sf-navbar__right">
            <div className="sf-navbar__lang" ref={langDropdownRef}>
              <button
                type="button"
                className="sf-navbar__lang-btn"
                onClick={() => setIsLangDropdownOpen((v) => !v)}
                aria-label="Change language"
              >
                <span className="sf-navbar__lang-flag">{currentLang.flag}</span>
                <span className="sf-navbar__lang-code">{currentLang.code.toUpperCase()}</span>
                <i className="fas fa-chevron-down sf-navbar__lang-caret" />
              </button>
              {isLangDropdownOpen && (
                <div className="sf-navbar__lang-menu">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`sf-navbar__lang-item ${
                        i18n.language === lang.code ? 'is-active' : ''
                      }`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      <span className="sf-navbar__lang-flag">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isConnected && (
              <Link to="/dashboard" className="sf-navbar__app-cta">
                <i className="fas fa-gauge-high" />
                <span>{t('nav.openApp')}</span>
              </Link>
            )}

            <div className="sf-navbar__connect">
              <ConnectButton
                showBalance={false}
                chainStatus="icon"
                accountStatus={{ smallScreen: 'avatar', largeScreen: 'address' }}
              />
            </div>

            <button
              type="button"
              className="sf-navbar__burger"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="sf-navbar__mobile">
            <ul>
              {NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(item.id);
                    }}
                  >
                    {t(item.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
